import type Pubnub from 'pubnub'
import type { MessageActionEvent } from 'pubnub'
import type {
  FlowExecutionCompleteEvent,
  NodeChunkCreatedEvent,
  NodeExecutedEvent,
  NodeUpdatedEvent,
  SystemChunkCreatedEvent,
} from 'v2/pages/flow/run-info/types'
import type { usePubnubMutations } from 'v2/shared/hooks/use-pubnub.mutations'
import type {
  RActiveConversation,
  RChunk,
  RMessageNotification,
  RNewConversation,
  RTask,
} from './api/conversations'
import type { Chunk, ChunkType } from './chunk.internal'

export interface FetchMessagesParams {
  channels: string[]
  start?: number
  end?: number
  count?: number
}

export type PubNubRef = {
  pubnub: Pubnub
  isRegistered: boolean
  isSubscribedToActiveConversations?: boolean
}

export interface PubNubContextType extends ReturnType<typeof usePubnubMutations> {
  pubnubRef: React.RefObject<PubNubRef>
}

export interface AsyncOutboundFailedEventData {
  channel: string
  contact_uuid: string
  error: string
}

export enum PubnubStatus {
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  IDLE = 'idle',
}

export interface TransferSuccessPrimaryData {
  conversation_number: string
  contact_uuid: string
  child_contact_uuid: string
  agent_uuid: string
  agent_name: string
  state: string
  status: string
  last_activity_at: string
}

export interface SecondaryParticipantHoldData {
  contact_uuid: string
  conversation_number: string
  hold: boolean
}

export interface TransferInitiatedPrimaryData {
  conversation_number: string
  contact_uuid: string
  child_contact_uuid: string
  agent_uuid: string
  agent_sip: string
  agent_name: string
  internal_chat_channel: string
  participant_identifier: string
  transfer_type: 'warm' | 'cold' | 'external number'
}

export interface TransferSecondaryLeftData {
  conversation_number: string
  contact_uuid: string
  child_contact_uuid: string
  agent_uuid: string
  agent_name: string
}

export enum PubnubEvents {
  // Conversation
  INVITE_AGENT = 'INVITE_AGENT',
  REMOVE_AGENT = 'REMOVE_AGENT',
  CONVERSATION_ACCEPTED = 'CONVERSATION_ACCEPTED',
  CONVERSATION_CLOSED = 'CONVERSATION_CLOSED',
  RACE_AGENT_JOIN = 'RACE_AGENT_JOIN',

  // SMS & Whatsapp
  MESSAGE_ACTION = 'MESSAGE_ACTION',
  MESSAGE_RECEIVED = 'MESSAGE_RECEIVED',
  MESSAGE_NOTIFICATION = 'MESSAGE_NOTIFICATION',
  ASYNC_OUTBOUND_FAILED = 'ASYNC_OUTBOUND_FAILED',
  CHUNK_ADDED = 'CHUNK_ADDED',

  // Chat
  CHAT_MESSAGE = 'CHAT_MESSAGE',

  // Transfer
  TRANSFER_CHUNK = 'TRANSFER_CHUNK',
  TRANSFER_SUCCESS_PRIMARY = 'TRANSFER_SUCCESS_PRIMARY',
  TRANSFER_SUCCESS_SECONDARY = 'TRANSFER_SUCCESS_SECONDARY', // When agent 2 accepts the transfer
  TRANSFER_INITIATED_PRIMARY = 'TRANSFER_INITIATED_PRIMARY', // Warm transfer is accepted
  TRANSFER_FAILED = 'TRANSFER_FAILED',
  TRANSFER_SECONDARY_LEFT = 'TRANSFER_SECONDARY_LEFT', // When agent 2 leaves the conversation
  SECONDARY_PARTICIPANT_ADDED = 'SECONDARY_PARTICIPANT_ADDED', // When agent 2 joins the conversation
  SECONDARY_PARTICIPANT_REMOVED = 'SECONDARY_PARTICIPANT_REMOVED', // When agent 2 leaves is removed
  SECONDARY_PARTICIPANT_HOLD = 'SECONDARY_PARTICIPANT_HOLD', // When agent 2 is put on hold
  TRANSFER_MERGE_SUCCESS = 'TRANSFER_MERGE_SUCCESS', // When call is merged, applicable only for warm call transfers, comes only to agent 2

  // Handoff context (external AI -> human): a versioned hint, the authorized read is truth
  HANDOFF_CONTEXT_UPDATED = 'HANDOFF_CONTEXT_UPDATED',

  // Custom
  CHUNKS_FETCHED = 'CHUNKS_FETCHED',
  ACTIVE_CONVERSATIONS_FETCHED = 'ACTIVE_CONVERSATIONS_FETCHED',

  // Call
  CCNS = 'CCNS', // Contacto call notification service, used to inform about incoming calls when sdk is not connected/registered

  OUTBOUND_CALL_CUSTOMER_ADDED = 'OUTBOUND_CALL_CUSTOMER_ADDED',
  OUTBOUND_CALL_FAILED = 'OUTBOUND_CALL_FAILED',
  AGENT_HANGUP = 'AGENT_HANGUP',
  OUTBOUND_CALL_INITIALISED = 'OUTBOUND_CALL_INITIALISED',
  OUTBOUND_CALL_ENDED = 'OUTBOUND_CALL_ENDED',

  CALL_RECORDING = 'CALL_RECORDING',

  // Monitoring
  MONITOR_FAILURE = 'MONITOR_FAILURE',
  SUPERVISOR_COACHING = 'SUPERVISOR_COACHING',
  SUPERVISOR_MONITORING = 'SUPERVISOR_MONITORING',
  SUPERVISOR_BARGING = 'SUPERVISOR_BARGING',
  ASSISTANCE_REQUESTED = 'ASSISTANCE_REQUESTED',
  ASSISTANCE_REJECTED = 'ASSISTANCE_REJECTED',
  SUPERVISOR_LEFT = 'SUPERVISOR_LEFT',
  TRANSFERRED_CONVERSATION = 'TRANSFERRED_CONVERSATION',

  // Task
  TASK_PENDING = 'TASK_PENDING',
  TASK_ACCEPTED = 'TASK_ACCEPTED',
  TASK_ACTIVITY_CREATED = 'TASK_ACTIVITY_CREATED',
  TASK_FAILED = 'TASK_FAILED',
  TASK_DECLINED = 'TASK_DECLINED',
  TASK_ROUTING_STATUS_UPDATED = 'TASK_ROUTING_STATUS_UPDATED',

  // Playground - Flow Run related
  PLAYGROUND_INITIATED = 'PLAYGROUND_INITIATED',
  NODE_EXECUTED = 'NODE_EXECUTED',
  // NODE_EXECUTION_STARTED = 'NODE_EXECUTION_STARTED',
  NODE_CHUNK_CREATED = 'NODE_CHUNK_CREATED',
  NODE_UPDATED = 'NODE_UPDATED',
  FLOW_EXECUTION_COMPLETE = 'FLOW_EXECUTION_COMPLETE',
  SYSTEM_CHUNK_CREATED = 'SYSTEM_CHUNK_CREATED',

  // Integration related
  INTEGRATION_SUCCESS = 'INTEGRATION_SUCCESS',
  INTEGRATION_UPDATED = 'INTEGRATION_UPDATED',
  INTEGRATION_FAILED = 'INTEGRATION_FAILED',
  INTEGRATION_ACCESS_UPDATED = 'INTEGRATION_ACCESS_UPDATED',

  // 10DLC / Toll-Free
  TDLC_STATUS_CHANGED = 'TDLC_STATUS_CHANGED',
  TDLC_CAMPAIGN_NUMBER_LINKED = 'TDLC_CAMPAIGN_NUMBER_LINKED',
}

interface PubnubTaskEventData {
  task: RTask
  chunk_data: RChunk
  parent_contact_uuid: string
  conversation_number: string
}

export interface IntegrationErrorEventData {
  error: string
}

export interface IntegrationSuccessEventData {
  message: string
}

// Change PubNubEventData to use a Record instead of a mapped type
export interface PubNubEventData {
  [PubnubEvents.INVITE_AGENT]: (data: RNewConversation) => void
  [PubnubEvents.REMOVE_AGENT]: (data: { contact_uuid: string }) => void
  [PubnubEvents.CONVERSATION_ACCEPTED]: (data: RActiveConversation) => void
  [PubnubEvents.CONVERSATION_CLOSED]: (data: {
    contact_uuid: string
    conversation_number: string
  }) => void
  [PubnubEvents.CHAT_MESSAGE]: (data: Chunk<ChunkType>) => void
  [PubnubEvents.MESSAGE_ACTION]: (data: MessageActionEvent) => void
  [PubnubEvents.MESSAGE_RECEIVED]: (data: RChunk) => void
  [PubnubEvents.MESSAGE_NOTIFICATION]: (data: RMessageNotification) => void
  [PubnubEvents.ACTIVE_CONVERSATIONS_FETCHED]: (data: {
    conversations: RActiveConversation[]
  }) => void
  [PubnubEvents.CHUNKS_FETCHED]: (data: { conversationNumber: string }) => void
  [PubnubEvents.ASYNC_OUTBOUND_FAILED]: (data: AsyncOutboundFailedEventData) => void
  [PubnubEvents.TRANSFER_CHUNK]: (data: { chunk_data: RChunk; conversation_number: string }) => void
  [PubnubEvents.OUTBOUND_CALL_ENDED]: (data: {
    conversation_number: string
    chunk_data: RChunk
  }) => void
  [PubnubEvents.CALL_RECORDING]: (data: { conversation_number: string; chunk_data: RChunk }) => void
  [PubnubEvents.TRANSFER_SUCCESS_PRIMARY]: (data: TransferSuccessPrimaryData) => void
  [PubnubEvents.RACE_AGENT_JOIN]: (data: { contact_uuid: string }) => void
  [PubnubEvents.TRANSFER_SUCCESS_SECONDARY]: (data: RActiveConversation) => void
  [PubnubEvents.TRANSFER_INITIATED_PRIMARY]: (data: TransferInitiatedPrimaryData) => void
  [PubnubEvents.TRANSFER_SECONDARY_LEFT]: (data: TransferSecondaryLeftData) => void
  [PubnubEvents.SECONDARY_PARTICIPANT_ADDED]: (data: RActiveConversation) => void
  [PubnubEvents.SECONDARY_PARTICIPANT_REMOVED]: (data: TransferSecondaryLeftData) => void
  [PubnubEvents.SECONDARY_PARTICIPANT_HOLD]: (data: SecondaryParticipantHoldData) => void
  [PubnubEvents.TRANSFER_MERGE_SUCCESS]: (data: {
    conversation_number: string
    contact_uuid: string
  }) => void
  [PubnubEvents.CHUNK_ADDED]: (data: RChunk) => void
  [PubnubEvents.HANDOFF_CONTEXT_UPDATED]: (data: {
    conversation_number: string
    content_version: number
  }) => void
  [PubnubEvents.TRANSFER_FAILED]: (data: {
    contact_uuid: string
    conversation_number: string
  }) => void
  [PubnubEvents.CCNS]: (data: {
    ccns_data: {
      index: number
      label: string
      registrar: string
    }
  }) => void

  [PubnubEvents.OUTBOUND_CALL_CUSTOMER_ADDED]: (payload: {
    contact_identifier: string
    contact_name: string
  }) => void
  [PubnubEvents.OUTBOUND_CALL_INITIALISED]: (payload: { conversation_number: string }) => void
  [PubnubEvents.OUTBOUND_CALL_FAILED]: () => void
  [PubnubEvents.AGENT_HANGUP]: () => void

  // Monitoring
  [PubnubEvents.MONITOR_FAILURE]: (data: {
    conversation_number: string
    error_message: string
    error_type: string
  }) => void
  [PubnubEvents.SUPERVISOR_COACHING]: (data: {
    conversation_number: string
    chunk_data: RChunk
  }) => void
  [PubnubEvents.SUPERVISOR_MONITORING]: (data: {
    conversation_number: string
    chunk_data: RChunk
  }) => void
  [PubnubEvents.SUPERVISOR_LEFT]: (data: {
    conversation_number: string
    chunk_data: RChunk
  }) => void
  [PubnubEvents.ASSISTANCE_REQUESTED]: (data: {
    agent_name: string
    conversation_number: string
    message: string
  }) => void
  [PubnubEvents.ASSISTANCE_REJECTED]: (data: { conversation_number: string }) => void
  [PubnubEvents.SUPERVISOR_BARGING]: (data: {
    conversation_number: string
    chunk_data: RChunk
  }) => void
  [PubnubEvents.TRANSFERRED_CONVERSATION]: (data: RActiveConversation) => void

  // Task related events
  [PubnubEvents.TASK_PENDING]: (data: PubnubTaskEventData) => void
  [PubnubEvents.TASK_ACTIVITY_CREATED]: (data: PubnubTaskEventData) => void
  [PubnubEvents.TASK_ACCEPTED]: (data: PubnubTaskEventData) => void
  [PubnubEvents.TASK_DECLINED]: (data: PubnubTaskEventData) => void
  [PubnubEvents.TASK_FAILED]: (data: PubnubTaskEventData) => void
  [PubnubEvents.TASK_ROUTING_STATUS_UPDATED]: (data: PubnubTaskEventData) => void

  // Flow run related - Playground
  [PubnubEvents.PLAYGROUND_INITIATED]: (data: unknown) => void
  [PubnubEvents.NODE_EXECUTED]: (data: NodeExecutedEvent) => void
  [PubnubEvents.NODE_CHUNK_CREATED]: (data: NodeChunkCreatedEvent) => void
  [PubnubEvents.NODE_UPDATED]: (data: NodeUpdatedEvent) => void
  [PubnubEvents.FLOW_EXECUTION_COMPLETE]: (data: FlowExecutionCompleteEvent) => void
  [PubnubEvents.SYSTEM_CHUNK_CREATED]: (data: SystemChunkCreatedEvent) => void

  // Integration related
  [PubnubEvents.INTEGRATION_SUCCESS]: (data: IntegrationSuccessEventData) => void
  [PubnubEvents.INTEGRATION_UPDATED]: () => void
  [PubnubEvents.INTEGRATION_FAILED]: (data: IntegrationErrorEventData) => void
  [PubnubEvents.INTEGRATION_ACCESS_UPDATED]: () => void

  // 10DLC / Toll-Free
  [PubnubEvents.TDLC_STATUS_CHANGED]: (data: {
    submission_uuid: string
    status: string
    registration_type: string
    /** Backend-decided list of tabs to refresh: subset of 'drafts' | 'brands' | 'tollfree' */
    affected_views: Array<'drafts' | 'brands' | 'tollfree'>
    campaign_id?: string | null
    brand_id?: string | null
    tollfree_verification_uuid?: string | null
  }) => void
  [PubnubEvents.TDLC_CAMPAIGN_NUMBER_LINKED]: (data: {
    campaign_id: string
    phone_number: string
    /** Which Plivo endpoint fired this — 'linking' or 'unlinking' */
    action: 'linking' | 'unlinking'
    /** Raw Plivo status (e.g. 'CREATED', 'FAILED', 'DELETED') — pass-through, not normalized */
    status: string
    reason?: string
  }) => void
}

export interface EventData<T extends PubnubEvents> {
  type: 'message' | 'signal'
  content: {
    eventName: T
    eventData?: Parameters<PubNubEventData[T]>[0]
    sender?: string // TODO_BE_v2: Remove this
    message?: string // TODO_BE_v2: Remove this, it should be in eventData
  }
  publishedTimestamp: number
}
